import type { AccountSubs } from './AccountSub.js'
import type { LoginDevice } from './Device.js'
import type { KeyInfo } from './KeyInfo.js'
import type { OnboardingSignupChoices } from './Onboarding.js'

export type Account = {
	readonly account_id: string
	readonly allocation_reset_ts: Maybe<number>
	readonly block_count: Maybe<number>
	readonly created: number
	readonly devices: string[]
	readonly emails: string[]
	readonly grant_count: Maybe<number>
	readonly last_activity: Maybe<number>
	readonly retired: Maybe<number>
	readonly retired_devices: Maybe<string[]>
	readonly retired_users: Maybe<string[]>
	readonly secret: Maybe<string>
	readonly skus: Maybe<AccountSubs>
	readonly users: string[]
}

export type AccountSSOStatus = {
	readonly account_exists: boolean
	readonly can_login_with_token: boolean
	readonly email: string
	readonly id_token: string
	readonly login_scheme: LoginScheme
	readonly token_valid: boolean
}

export type SSOLoginScheme = 'sso_apple' | 'sso_google'
export type LoginScheme = SSOLoginScheme | 'hash2' | ''

export type SSOLoginParams = {
	readonly id_token: string
	readonly type: SSOLoginScheme
}

export type SSOLoginBody = {
	readonly device?: LoginDevice
	readonly login_obj: { readonly sso: SSOLoginParams }
}

/**
 * The `AuthenticatedAccount` is what we get when logging in. This provides the
 * `Account` object with some additional fields.
 */
export type AuthenticatedAccount = Account & {
	readonly acctkey?: KeyInfo
	readonly login_scheme: LoginScheme
	readonly parent_secret: string
	readonly parent_secret_id: string
	readonly token: string
}

type AdAttribution = {
	// currently we only have Google Ads support
	readonly type: 'googleads'
	readonly gclid: string
}

// NOTE: The server calls this `user_obj`
type BaseNewAccount = {
	readonly attribution?: AdAttribution
	readonly coupon_code?: string
	readonly group?: string
	readonly locale: string
	readonly onboarding_choices?: OnboardingSignupChoices
	readonly time_zone: string
	readonly user_name: string
}

export type NewPasswordAccount = BaseNewAccount & {
	readonly email: string
	readonly password: string
}

export type NewSSOAccount = BaseNewAccount & {
	readonly sso: SSOLoginParams
}

export type NewAccount = NewPasswordAccount | NewSSOAccount

export const isSSOLoginScheme = (scheme: any): scheme is SSOLoginScheme =>
	scheme == 'sso_apple' || scheme == 'sso_google'
